import { GroupType, GroupVisibility } from '@bp/bp-graphql-types';
import { graphql } from 'client/gql';
import { useCoursesTable } from 'components/Courses/hooks/useCoursesTable';
import { CourseEditorsModal } from 'components/Courses/modals/CourseEditorsModal';
import { CourseModal } from 'components/Courses/modals/CourseModal';
import { CourseSettingsModal } from 'components/Courses/modals/CourseSettingsModal';
import { CourseViewersModal } from 'components/Courses/modals/CourseViewersModal';
import { CoursesStudentTable } from 'components/Courses/tables/CoursesStudentTable';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { showSuccessDeleteToast, showUserDbErrorToast } from 'utils/toasts';
import { BpPage } from '../../components/BpPage/BpPage';
import { CoursesTeacherTable } from '../../components/Courses/tables/CoursesTeacherTable';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { useAuthClaims } from '../../hooks/useAuthClaims';

const DeleteGroupMutationDocument = graphql(`
  mutation BpDeleteGroupCoursesPage($id: ID!) {
    deleteBpGroup(uuid: $id) {
      entitiesDeleted
    }
  }
`);

const CoursesPageDataDocument = graphql(`
  query CoursesPageData($courseWhere: GroupWhere!, $organizationUuid: ID!, $courseUuid: ID!) {
    ...UseCoursesTableFields
    ...CourseEditorsFormFields
    ...CourseViewersFormFields
  }
`);

export const CoursesPage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims, updateClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { currentSchoolYear, schoolyears } = useContext(OrganizationConfigContext);
  const navigate = useNavigate();

  const { ConfirmationDialog, confirmDelete } = useCoursesTable();

  const [, deleteCourse] = useMutation(DeleteGroupMutationDocument);

  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [selectedYearUuid, setSelectedYearUuid] = useState<string>(currentSchoolYear.uuid);
  const [claimsTrigger, setClaimsTrigger] = useState<number>(0);
  const [courseUuid, setCourseUuid] = useState<string | null>(null);
  const [courseModalOpen, setCourseModalOpen] = useState<boolean>(false);
  const [editorsModalOpen, setEditorsModalOpen] = useState<boolean>(false);
  const [viewersModalOpen, setViewersModalOpen] = useState<boolean>(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);

  const permissions = usePermissionChecker();

  // this serves as filter for hidden courses for students
  const visibilityOR = [{ visibility: GroupVisibility.Full }];
  if (permissions?.canCreateCourse()) visibilityOR.push({ visibility: GroupVisibility.Hidden });

  const [{ data }] = useQuery({
    query: CoursesPageDataDocument,
    variables: {
      organizationUuid: organizationUuid,
      schoolYearUuid: selectedYearUuid,
      courseUuid: courseUuid ?? '',
      courseWhere: {
        OR: visibilityOR,
        type: GroupType.Course,
        organization: { uuid_IN: pimAuthClaims.getAllowedOrganizationUuids() },
        AND: [
          {
            OR: [
              { editors_SOME: { uuid: profileUuid } },
              { viewers_SOME: { uuid: profileUuid } },
              { admins_SOME: { uuid: profileUuid } },
            ],
          },
          { OR: [{ schoolYear: { uuid: selectedYearUuid } }] },
        ],
      },
    },
  });

  function handleAdd() {
    setCourseUuid(null);
    setCourseModalOpen(true);
  }

  function handleEdit(uuid: string) {
    setCourseUuid(uuid);
    setCourseModalOpen(true);
  }

  function handleSettings(uuid: string) {
    setCourseUuid(uuid);
    setSettingsModalOpen(true);
  }

  async function handleDelete(uuid: string, name: string) {
    const result = await confirmDelete({
      title: t('courses.delete.title'),
      message: t('courses.delete.message', { name }),
      confirmText: t('courses.delete.confirmText'),
    });
    if (!result) return;
    setTableLoading(true);
    const { error } = await deleteCourse({ id: uuid });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }
    setTableLoading(false);
  }

  function handleNavigate(uuid: string) {
    navigate('/courses/' + uuid);
  }

  async function handleClose(withClaims: boolean) {
    setCourseModalOpen(false);
    setEditorsModalOpen(false);
    setViewersModalOpen(false);
    setSettingsModalOpen(false);
    setCourseUuid(null);
    if (withClaims) {
      await updateClaims(() => setClaimsTrigger(claimsTrigger + 1));
    }
  }

  return (
    <BpPage
      title={t('courses.myCourses', {
        year: schoolyears?.find((y) => y.uuid === selectedYearUuid)?.name ?? currentSchoolYear?.name,
      })}
      isForbidden={false}
      organization={pimAuthClaims.getOrganization()}
    >
      {permissions?.canCreateCourse() ? (
        <CoursesTeacherTable
          query={data}
          permissions={permissions}
          loading={tableLoading}
          yearUuid={selectedYearUuid}
          onYearSelect={setSelectedYearUuid}
          onNavigate={handleNavigate}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onSettings={handleSettings}
        />
      ) : (
        <CoursesStudentTable
          query={data}
          yearUuid={selectedYearUuid}
          onYearSelect={setSelectedYearUuid}
          onNavigate={handleNavigate}
        />
      )}

      <CourseModal
        isOpen={courseModalOpen}
        courseUuid={courseUuid}
        schoolYearUuid={selectedYearUuid}
        page='courses'
        onClose={() => handleClose(true)}
      />

      {courseUuid && (
        <CourseEditorsModal
          isOpen={editorsModalOpen}
          courseUuid={courseUuid}
          schoolYearUuid={selectedYearUuid}
          onClose={() => handleClose(true)}
          query={data}
        />
      )}

      {courseUuid && (
        <CourseViewersModal
          isOpen={viewersModalOpen}
          courseUuid={courseUuid}
          schoolYearUuid={selectedYearUuid}
          onClose={() => handleClose(true)}
          query={data}
        />
      )}

      {courseUuid && (
        <CourseSettingsModal isOpen={settingsModalOpen} courseUuid={courseUuid} onClose={() => handleClose(false)} />
      )}

      <ConfirmationDialog />
    </BpPage>
  );
};
